<template>
    <div>
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-2">
                    Categories
                </h2>
                <p>
                    {{ subjectArea.name }}
                </p>
            </div>
            <div>
                <div class="flex items-center gap-3">
                    <!-- Search Filter -->
                    <div class="dropdown dropdown-end">
                        <d-btn flat size="sm">
                            <d-icon icon="filter" />
                        </d-btn>
                        <div tabindex="0" class="dropdown-content z-[1] card card-compact w-64 p-4 shadow bg-base-100">
                            <d-field label="Search by Name">
                                <d-input 
                                    v-model="filter" 
                                    placeholder="Type to search..."
                                    size="sm"
                                />
                            </d-field>
                        </div>
                    </div>
                    
                    <!-- Add Category Button -->
                    <d-btn 
                        color="secondary"
                        @click="openCategoryModal()"
                    >
                        <d-icon icon="plus" />
                        Add Category
                    </d-btn>
                </div>
            </div>
        </div>
        
        <!-- Categories Table -->
        <d-table 
            class="bg-white shadow mt-10 mb-5" 
            :data="categories" 
            :columns="columns"
            :loading="loading" 
            backend-search 
            backend-sorting 
            :default-sort="[pagination.sortField, pagination.sortOrder]" 
            striped 
            default-sort-direction="desc" 
            @sort="onSort"
        >
            <template v-slot="{ row }">
                <td>
                    <d-icon :icon="row.imageUrl" pack="fad" class="ml-2 text-xl text-secondary-500" size="1x" />
                </td>
                <td>{{ row.name }}</td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="primary"
                        @click="openCategoryAccess(row)"
                    >
                        <d-icon icon="users" />
                    </d-btn>
                </td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="primary"
                        @click="openCategoryModal(row)"
                    >
                        <d-icon icon="edit" />
                    </d-btn>
                </td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="error"
                        @click="confirmDelete(row)"
                    >
                        <d-icon icon="trash" />
                    </d-btn>
                </td>
            </template>
        </d-table>
        
        <!-- Pagination -->
        <div class="flex justify-end mt-6">
            <d-pagination 
                :total-items="total" 
                :per-page="pagination.pageSize" 
                v-model:current="pagination.page" 
            />
        </div>

        <!-- Category Modal -->
        <category-modal
            :active="categoryModalActive"
            :category="selectedCategory"
            :subject-area="subjectArea"
            @close="closeCategoryModal"
            @saved="onCategorySaved"
        />

        <!-- Category Access Modal -->
        <category-access-modal
            :active="categoryAccessModalActive"
            :category="selectedCategoryForAccess"
            :subject-area="subjectArea"
            @close="closeCategoryAccess"
            @saved="onCategorySaved"
        />

        <!-- Delete Confirmation Modal -->
        <confirm-modal
            :active="deleteModalActive"
            :title="'Delete Category'"
            :message="`Are you sure you want to delete <strong>${categoryToDelete?.name}</strong>?<br><br>This action cannot be undone.`"
            :confirm-text="'Delete Category'"
            @close="closeDeleteModal"
            @confirm="handleDeleteConfirmed"
        />
    </div>
</template>

<script>
  import axios from 'axios';
  import { toast } from '@services/toast.service';
  import CategoryModal from './components/CategoryModal.vue';
  import CategoryAccessModal from './components/CategoryAccessModal.vue';
  import ConfirmModal from '../Users/components/ConfirmModal.vue';

  export default {
    name: 'CategoriesPage',
    
    components: {
      CategoryModal,
      CategoryAccessModal,
      ConfirmModal,
    },
    
    props: {
      subjectArea: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        categories: [],
        loading: false,
        filter: '',
        total: 0,
        categoryModalActive: false,
        selectedCategory: null,
        categoryAccessModalActive: false,
        selectedCategoryForAccess: null,
        deleteModalActive: false,
        categoryToDelete: null,
        pagination: {
          page: 1,
          pageSize: 20,
          sortField: 'name',
          sortOrder: 'asc',
        },
      };
    },

    computed: {
      columns() {
        return [
          { field: 'imageUrl', label: 'Icon' },
          { field: 'name', label: 'Name', sortable: true },
          { field: 'groups', label: 'Groups' },
          { field: 'edit', label: 'Edit' },
          { field: 'delete', label: 'Delete' },
        ];
      }
    },

    watch: {
      pagination: {
        handler() {
          this.getCategoriesPaged();
        },
        deep: true,
      },
      filter() {
        this.pagination.page = 1; // Reset to first page when filtering
        this.getCategoriesPaged();
      },
    },

    mounted() {
      this.getCategoriesPaged();
    },

    methods: {
      async getCategoriesPaged() {
        const { page, sortField, sortOrder, pageSize } = this.pagination;
        const params = [
          `filter=${encodeURIComponent(this.filter)}`,
          `sortDir=${sortField} ${sortOrder}`,
          `currentPage=${page}`,
          `pageSize=${pageSize}`
        ].join('&');
        
        this.loading = true;
        try {
          const { data } = await axios.get(`/api/subject-areas/${this.subjectArea.id}/categories/paged?${params}`);
          this.categories = data.resultItems || [];
          this.total = data.totalRecords || 0;
        } catch (error) {
          console.error('Error loading categories:', error);
          toast.error('Failed to load categories');
          this.categories = [];
          this.total = 0;
        } finally {
          this.loading = false;
        }
      },

      openCategoryModal(category = null) {
        this.selectedCategory = category;
        this.categoryModalActive = true;
      },

      closeCategoryModal() {
        this.categoryModalActive = false;
        this.selectedCategory = null;
      },

      openCategoryAccess(category) {
        this.selectedCategoryForAccess = category;
        this.categoryAccessModalActive = true;
      },

      closeCategoryAccess() {
        this.categoryAccessModalActive = false;
        this.selectedCategoryForAccess = null;
      },

      onCategorySaved() {
        this.getCategoriesPaged();
        this.closeCategoryModal();
        this.closeCategoryAccess();
      },

      confirmDelete(category) {
        this.categoryToDelete = category;
        this.deleteModalActive = true;
      },

      closeDeleteModal() {
        this.deleteModalActive = false;
        this.categoryToDelete = null;
      },

      async handleDeleteConfirmed() {
        if (!this.categoryToDelete) return;
        
        try {
          await axios.delete(`/api/subject-areas/categories/${this.categoryToDelete.id}`);
          await this.getCategoriesPaged();
          toast.success('Category deleted successfully');
        } catch (error) {
          console.error('Error deleting category:', error);
          toast.error('Failed to delete category');
        } finally {
          this.closeDeleteModal();
        }
      },

      onSort(field, order) {
        this.pagination.sortField = field;
        this.pagination.sortOrder = order;
      },
    },
  };
</script>